import { useEffect, useMemo, useState } from "react";
import { Activity, Check, Loader2, RotateCcw, Search, Settings2 } from "lucide-react";
import { vitalMeasurementService } from "../services/vitalMeasurementService";

const asBool = (value) => value === true || value === 1 || value === "1" || value === "true";
const asText = (value) => (value == null ? "" : String(value));

const normalize = (row) => ({
  ...row,
  id: Number(row.id),
  displayName: row.displayName ?? row.display_name ?? row.name ?? "",
  defaultUnit: row.defaultUnit ?? row.default_unit ?? "",
  unit: row.unit ?? row.unit_override ?? row.defaultUnit ?? row.default_unit ?? "",
  companyDefaultEnabled: asBool(row.companyDefaultEnabled ?? row.company_default_enabled),
  enabled: asBool(row.enabledByDefault ?? row.enabled_by_default ?? row.is_enabled ?? row.companyDefaultEnabled),
  required: asBool(row.required ?? row.is_required),
  hasDoctorOverride: asBool(row.hasDoctorOverride ?? row.has_doctor_override),
  conversionFactor: row.conversionFactor ?? row.conversion_factor ?? "",
  conversionOffset: row.conversionOffset ?? row.conversion_offset ?? "",
  minimumValue: row.minimumValue ?? row.minimum_value ?? "",
  maximumValue: row.maximumValue ?? row.maximum_value ?? "",
  sortOrder: row.sortOrder ?? row.sort_order ?? 0,
  companyDefaultSortOrder: row.companyDefaultSortOrder ?? row.company_default_sort_order ?? row.sortOrder ?? row.sort_order ?? 0,
});

export default function DoctorVitalsSettings({ doctorId, clinicId }) {
  const [measurements, setMeasurements] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All categories");
  const [showAllMeasurements, setShowAllMeasurements] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [dirtyIds, setDirtyIds] = useState(() => new Set());
  const [resetIds, setResetIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = async () => {
    if (!doctorId || !clinicId) {
      setMeasurements([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const result = await vitalMeasurementService.getDoctorSettings(doctorId, clinicId);
      setMeasurements((Array.isArray(result) ? result : []).map(normalize));
      setDirtyIds(new Set());
      setResetIds(new Set());
    } catch (loadError) {
      setError(loadError?.response?.data?.message || "Unable to load this doctor's measurement settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let live = true;
    if (!doctorId || !clinicId) {
      setMeasurements([]);
      setLoading(false);
      return () => { live = false; };
    }
    setLoading(true);
    setError("");
    vitalMeasurementService.getDoctorSettings(doctorId, clinicId)
      .then((result) => {
        if (live) setMeasurements((Array.isArray(result) ? result : []).map(normalize));
      })
      .catch((loadError) => {
        if (live) setError(loadError?.response?.data?.message || "Unable to load this doctor's measurement settings.");
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [doctorId, clinicId]);

  const categories = useMemo(
    () => ["All categories", ...Array.from(new Set(measurements.map((item) => item.category).filter(Boolean))).sort()],
    [measurements],
  );
  const filtered = useMemo(() => measurements.filter((item) => {
    const matchesCategory = category === "All categories" || item.category === category;
    const query = search.trim().toLowerCase();
    const configured = item.companyDefaultEnabled || item.hasDoctorOverride || (item.custom && item.enabled);
    return (showAllMeasurements || Boolean(query) || configured)
      && matchesCategory
      && (!query || `${item.displayName} ${item.category} ${item.specialtyTags || ""}`.toLowerCase().includes(query));
  }), [category, measurements, search, showAllMeasurements]);

  const update = (id, patch) => {
    setMeasurements((current) => current.map((item) => item.id === id ? { ...item, ...patch, hasDoctorOverride: true } : item));
    setDirtyIds((current) => new Set(current).add(id));
    setResetIds((current) => { const next = new Set(current); next.delete(id); return next; });
    setNotice("");
  };

  const resetToCompanyDefault = (item) => {
    setMeasurements((current) => current.map((row) => row.id === item.id
      ? { ...row, enabled: row.companyDefaultEnabled, displayName: row.name, unit: row.defaultUnit, required: false, conversionFactor: "", conversionOffset: "", minimumValue: "", maximumValue: "", sortOrder: row.companyDefaultSortOrder }
      : row));
    setDirtyIds((current) => { const next = new Set(current); next.delete(item.id); return next; });
    setResetIds((current) => new Set(current).add(item.id));
    setNotice("");
  };

  const save = async () => {
    if (!doctorId || !clinicId || (!dirtyIds.size && !resetIds.size)) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const changed = measurements.filter((item) => dirtyIds.has(item.id)).map((item) => ({
        measurementId: item.id,
        enabled: item.enabled,
        displayName: item.displayName,
        unit: item.unit,
        conversionFactor: item.conversionFactor === "" ? null : item.conversionFactor,
        conversionOffset: item.conversionOffset === "" ? null : item.conversionOffset,
        required: item.required,
        minimumValue: item.minimumValue === "" ? null : item.minimumValue,
        maximumValue: item.maximumValue === "" ? null : item.maximumValue,
        sortOrder: item.sortOrder === "" ? null : Number(item.sortOrder),
      }));
      if (changed.length) await vitalMeasurementService.saveDoctorSettings(doctorId, clinicId, changed);
      await Promise.all(Array.from(resetIds, (id) => vitalMeasurementService.resetDoctorSetting(doctorId, clinicId, id)));
      await load();
      setNotice("Vitals settings saved.");
    } catch (saveError) {
      setError(saveError?.response?.data?.message || "Unable to save vitals settings.");
    } finally {
      setSaving(false);
    }
  };

  const pendingChanges = dirtyIds.size + resetIds.size;

  return (
    <section className="dvs-root" aria-label="Doctor vitals settings" onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); event.stopPropagation(); } }}>
      <style>{`
        .dvs-root{display:flex;flex-direction:column;gap:14px;color:#14253d;font-family:inherit}
        .dvs-heading{display:flex;align-items:center;gap:11px}
        .dvs-heading-icon{width:36px;height:36px;display:grid;place-items:center;background:#eaf2ff;color:#2563eb;border:1px solid #d5e4ff;border-radius:10px;flex:none}
        .dvs-heading strong{display:block;font-size:14px;font-weight:800}
        .dvs-heading small{display:block;margin-top:3px;color:#697a91;font-size:11px;line-height:1.45}
        .dvs-toolbar{display:grid;grid-template-columns:minmax(0,1fr) minmax(145px,.65fr);gap:8px}
        .dvs-search{height:38px;border:1px solid #d8e0ea;border-radius:8px;display:flex;align-items:center;gap:8px;padding:0 10px;background:#fff;color:#718096}
        .dvs-search input,.dvs-filter{min-width:0;width:100%;height:38px;border:1px solid #d8e0ea;border-radius:8px;background:#fff;color:#25364b;font:inherit;font-size:12px;padding:0 10px;outline:none}
        .dvs-search input{height:auto;border:0;padding:0}
        .dvs-browse{align-self:flex-start;padding:0;border:0;background:transparent;color:#2563eb;font:inherit;font-size:10px;font-weight:750;cursor:pointer}
        .dvs-search:focus-within,.dvs-filter:focus{border-color:#7ca7f8;box-shadow:0 0 0 3px #eaf2ff}
        .dvs-list{display:flex;flex-direction:column;gap:7px}
        .dvs-row{border:1px solid #e0e7f0;border-radius:9px;background:#fff;overflow:hidden}
        .dvs-row-head{display:flex;align-items:center;gap:10px;padding:10px 11px;min-height:54px}
        .dvs-toggle{position:relative;width:36px;height:21px;flex:none}
        .dvs-toggle input{position:absolute;inset:0;margin:0;opacity:0;cursor:pointer}
        .dvs-toggle i{position:absolute;inset:0;border-radius:20px;background:#cbd5e1;transition:.16s;pointer-events:none}
        .dvs-toggle i:after{content:"";position:absolute;left:3px;top:3px;width:15px;height:15px;border-radius:50%;background:white;box-shadow:0 1px 2px #0002;transition:.16s}
        .dvs-toggle input:checked+i{background:#2563eb}
        .dvs-toggle input:checked+i:after{transform:translateX(15px)}
        .dvs-toggle input:focus-visible+i{outline:3px solid #bfdbfe;outline-offset:2px}
        .dvs-row-name{flex:1;min-width:0}
        .dvs-row-name strong{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:12px;font-weight:750}
        .dvs-row-name small{display:block;margin-top:3px;color:#718096;font-size:10px}
        .dvs-default{padding:3px 6px;border:1px solid #e2e8f0;border-radius:5px;color:#63748b;font-size:9px;white-space:nowrap}
        .dvs-edit{width:30px;height:30px;display:grid;place-items:center;border:1px solid transparent;border-radius:7px;background:transparent;color:#60728a;cursor:pointer;flex:none}
        .dvs-edit:hover{background:#f1f5f9;border-color:#e2e8f0}
        .dvs-detail{padding:11px;border-top:1px solid #edf1f6;background:#f8fafc;display:grid;grid-template-columns:1fr 1fr;gap:9px}
        .dvs-field{display:flex;flex-direction:column;gap:4px;min-width:0;color:#62738b;font-size:10px;font-weight:700}
        .dvs-field input{width:100%;height:34px;border:1px solid #d7e0eb;border-radius:7px;background:white;padding:0 9px;color:#21344e;font:inherit;font-size:11px;outline:none}
        .dvs-field input:focus{border-color:#7ca7f8;box-shadow:0 0 0 3px #eaf2ff}
        .dvs-detail-note{grid-column:1/-1;color:#718096;font-size:10px;line-height:1.45}
        .dvs-check{grid-column:1/-1;display:flex;align-items:center;gap:8px;color:#364963;font-size:11px;font-weight:650}
        .dvs-check input{accent-color:#2563eb}
        .dvs-actions{display:flex;justify-content:space-between;align-items:center;gap:8px;padding-top:2px}
        .dvs-actions small{color:#718096;font-size:10px}
        .dvs-save{height:36px;padding:0 12px;display:inline-flex;align-items:center;gap:6px;border:0;border-radius:8px;background:#2563eb;color:white;font:inherit;font-size:11px;font-weight:750;cursor:pointer}
        .dvs-save:disabled{opacity:.45;cursor:not-allowed}
        .dvs-reset{grid-column:1/-1;justify-self:start;border:0;background:transparent;color:#2563eb;font:inherit;font-size:10px;font-weight:700;cursor:pointer;padding:3px 0}
        .dvs-alert{padding:9px 10px;border-radius:7px;background:#fff1f0;color:#b42318;font-size:11px;line-height:1.4}
        .dvs-success{color:#157347;font-size:11px;font-weight:650}
        .dvs-empty{padding:18px;text-align:center;border:1px dashed #d8e0ea;border-radius:9px;color:#718096;font-size:11px}
        @media(max-width:520px){.dvs-toolbar{grid-template-columns:1fr}.dvs-row-head{gap:7px;padding:9px}.dvs-default{display:none}.dvs-detail{grid-template-columns:1fr 1fr}}
      `}</style>
      <div className="dvs-heading">
        <span className="dvs-heading-icon"><Activity size={17} /></span>
        <span><strong>Vitals & measurements</strong><small>Choose what this doctor sees by default. Company defaults are a starting point; every measurement can be changed.</small></span>
      </div>

      {!doctorId && <div className="dvs-empty">Save this doctor profile before configuring measurements.</div>}
      {error && <div className="dvs-alert" role="alert">{error}</div>}
      {notice && <div className="dvs-success" role="status">{notice}</div>}

      {doctorId && <>
        <div className="dvs-toolbar">
          <label className="dvs-search"><Search size={14} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search measurements" aria-label="Search measurements" /></label>
          <select className="dvs-filter" value={category} onChange={(event) => setCategory(event.target.value)} aria-label="Filter by category">
            {categories.map((item) => <option key={item}>{item}</option>)}
          </select>
        </div>
        <button type="button" className="dvs-browse" onClick={() => { setShowAllMeasurements((current) => !current); setSearch(""); }}>
          {showAllMeasurements ? "Show configured measurements" : "Browse all measurements"}
        </button>

        {loading ? <div className="dvs-empty"><Loader2 size={15} className="animate-spin" /> Loading measurement settings…</div> : (
          <div className="dvs-list">
            {filtered.map((item) => {
              const expanded = expandedId === item.id;
              const pendingReset = resetIds.has(item.id);
              return (
                <article className="dvs-row" key={item.id}>
                  <div className="dvs-row-head">
                    <label className="dvs-toggle" title={`${item.enabled ? "Disable" : "Enable"} ${item.displayName}`}>
                      <input type="checkbox" checked={item.enabled} onChange={(event) => update(item.id, { enabled: event.target.checked })} aria-label={`${item.enabled ? "Disable" : "Enable"} ${item.displayName}`} />
                      <i />
                    </label>
                    <span className="dvs-row-name"><strong>{item.displayName || item.name}</strong><small>{item.category} · {item.unit || "No unit"}</small></span>
                    <span className="dvs-default">Company default {item.companyDefaultEnabled ? "On" : "Off"}</span>
                    <button type="button" className="dvs-edit" onClick={() => setExpandedId(expanded ? null : item.id)} aria-expanded={expanded} aria-label={`Edit ${item.displayName} settings`}><Settings2 size={15} /></button>
                  </div>
                  {expanded && <div className="dvs-detail">
                    <label className="dvs-field">Display name<input maxLength={120} value={item.displayName || ""} onChange={(event) => update(item.id, { displayName: event.target.value })} /></label>
                    <label className="dvs-field">Unit (custom units allowed)<input maxLength={80} value={item.unit || ""} placeholder="No unit" onChange={(event) => update(item.id, { unit: event.target.value })} /></label>
                    <label className="dvs-field">Conversion factor<input inputMode="decimal" value={asText(item.conversionFactor)} onChange={(event) => update(item.id, { conversionFactor: event.target.value })} placeholder="Optional" /></label>
                    <label className="dvs-field">Conversion offset<input inputMode="decimal" value={asText(item.conversionOffset)} onChange={(event) => update(item.id, { conversionOffset: event.target.value })} placeholder="Optional" /></label>
                    <label className="dvs-field">Minimum value<input inputMode="decimal" value={asText(item.minimumValue)} onChange={(event) => update(item.id, { minimumValue: event.target.value })} placeholder="Optional" /></label>
                    <label className="dvs-field">Maximum value<input inputMode="decimal" value={asText(item.maximumValue)} onChange={(event) => update(item.id, { maximumValue: event.target.value })} placeholder="Optional" /></label>
                    <label className="dvs-field">Display order<input type="number" value={asText(item.sortOrder)} onChange={(event) => update(item.id, { sortOrder: event.target.value })} /></label>
                    <label className="dvs-check"><input type="checkbox" checked={item.required} onChange={(event) => update(item.id, { required: event.target.checked })} /> Required when this measurement is enabled</label>
                    <div className="dvs-detail-note">Values are kept with the unit used at recording time. Set a conversion factor/offset to enable canonical numeric comparisons. {item.defaultUnit ? `Catalog unit: ${item.defaultUnit}.` : "This measurement has no catalog unit."}</div>
                    {(item.hasDoctorOverride || pendingReset) && <button type="button" className="dvs-reset" onClick={() => resetToCompanyDefault(item)}><RotateCcw size={12} /> Reset to company default</button>}
                  </div>}
                </article>
              );
            })}
            {!filtered.length && !loading && <div className="dvs-empty">No measurements match this search.</div>}
          </div>
        )}

        <div className="dvs-actions">
          <small>{measurements.filter((item) => item.enabled).length} enabled · {pendingChanges ? `${pendingChanges} unsaved change${pendingChanges === 1 ? "" : "s"}` : "All changes saved"}</small>
          <button type="button" className="dvs-save" disabled={!pendingChanges || saving || loading} onClick={save}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}{saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      </>}
    </section>
  );
}
